import type {
  AgentWorkspaceApproval,
  MessageActivityEvent,
} from "@lumen/shared";
import { t } from "../../i18n";
import { Markdown } from "../Markdown";
import { MemoryActivityCard } from "./MemoryActivityCard";
import { ContextCompactionCard } from "./ContextCompactionCard";
import { ThinkingBlock } from "./ThinkingBlock";
import { ToolActivityCard } from "./ToolActivityCard";
import type { ApprovalHandler, QuestionAnswerHandler } from "./MessageActivity";

export function ActivityEventCard({
  event,
  busy,
  streaming = busy,
  compact = false,
  approvals,
  decidingApprovalIds,
  onApproval,
  onContinue,
  onQuestionAnswer,
}: {
  event: MessageActivityEvent;
  busy: boolean;
  streaming?: boolean;
  compact?: boolean;
  approvals: Map<string, AgentWorkspaceApproval>;
  decidingApprovalIds: Set<string>;
  onApproval?: ApprovalHandler;
  onContinue?: () => void;
  onQuestionAnswer?: QuestionAnswerHandler;
}) {
  if (event.type === "text") {
    const content = <Markdown streaming={streaming}>{event.content}</Markdown>;
    return compact ? (
      <div className="rounded-lg bg-white/70 p-3 text-sm dark:bg-black/10">
        {content}
      </div>
    ) : (
      content
    );
  }
  if (event.type === "reasoning")
    return (
      <ThinkingBlock
        content={event.content}
        streaming={streaming}
        label={compact ? t("Subagent thinking") : undefined}
        compact={compact}
      />
    );
  if (event.type === "memory") return <MemoryActivityCard event={event} />;
  if (event.type === "error")
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
        <strong>{t("Provider error")}</strong>
        <br />
        {event.error}
      </div>
    );
  if (event.type === "compaction")
    return (
      <ContextCompactionCard
        event={event}
        messageContent={event.summary ?? ""}
      />
    );
  if (event.type === "tool") {
    if (!event.tool) return invalidActivityEvent(event);
    const tool = event.tool;
    return (
      <ToolActivityCard
        event={tool}
        approval={tool.approvalId ? approvals.get(tool.approvalId) : undefined}
        busy={
          busy ||
          Boolean(tool.approvalId && decidingApprovalIds.has(tool.approvalId))
        }
        onApproval={onApproval}
        onContinue={onContinue}
        onQuestionAnswer={onQuestionAnswer}
      />
    );
  }
  return invalidActivityEvent(event);
}

function invalidActivityEvent(event: unknown) {
  let serialized = "Unable to serialize activity event";
  try {
    serialized = JSON.stringify(event, null, 2) ?? String(event);
  } catch {
    // Keep the diagnostic visible even if a future event contains cycles.
  }
  return (
    <details className="rounded-lg border border-amber-200 bg-amber-50/70 p-2 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/20 dark:text-amber-200">
      <summary className="cursor-pointer font-semibold">
        {t("Unrendered activity event")}
      </summary>
      <pre className="mt-2 max-h-64 overflow-auto text-[11px] whitespace-pre-wrap">
        {serialized}
      </pre>
    </details>
  );
}
